import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Header.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faChevronDown,
  faShoppingCart,
  faPhone,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useLocalContext } from "../../context/LocalContext";
import { useProduct } from "../../context/ProductContext";

const Header = () => {
  const navigate = useNavigate();
  const { cart, selectedAntiv, setSelectedAntiv } = useProduct();
  const { webinfo = {} } = useLocalContext();

  const [openCat, setOpenCat] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const catBtnRef = useRef(null);

  const categories = [
    { id: "01", name: "McAfee", route: "/mcafee" },
    { id: "02", name: "Norton", route: "/norton" },
    { id: "03", name: "Avast", route: "/avast" },
    { id: "04", name: "Kaspersky", route: "/kaspersky" },
    { id: "05", name: "AVG", route: "/avg" },
    { id: "06", name: "Bitdefender", route: "/bitdefender" },
    { id: "07", name: "TotalAV", route: "/totalav" },
  ];

  const cartLength = cart.length;

  const go = (path) => {
    setMobileOpen(false);
    navigate(path);
  };

  const handleCategoryNavigation = (category) => {
    setMobileOpen(false);
    setSelectedAntiv(category.name);
    navigate(category.route);
  };

  // close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (!catBtnRef.current) return;
      if (!catBtnRef.current.closest) return;
      const dropdown = document.querySelector(".hd-mega");
      if (
        dropdown &&
        !dropdown.contains(e.target) &&
        !catBtnRef.current.contains(e.target)
      ) {
        setOpenCat(false);
      }
    };
    document.addEventListener("click", onDoc);
    return () => document.removeEventListener("click", onDoc);
  }, []);

  return (
    <header className="hd">
      {/* subtle backdrop */}
      <div className="hd-bg" aria-hidden="true" />

      <div className="hd-wrap">
        {/* LOGO */}
        <button className="hd-logo" onClick={() => go("/")}>
          {webinfo?.logo ? (
            <img src={webinfo.logo} alt={webinfo.name || "Logo"} />
          ) : (
            <span>{webinfo?.name || "SecureHub"}</span>
          )}
        </button>

        {/* DESKTOP NAV */}
        <nav className="hd-nav" aria-label="Primary">
          <button className="hd-link" onClick={() => go("/")}>Home</button>

          <div className="hd-drop">
            <button
              ref={catBtnRef}
              className={`hd-link hd-link--drop ${openCat ? "is-open" : ""}`}
              onClick={() => setOpenCat((v) => !v)}
              onMouseEnter={() => setOpenCat(true)}
              aria-expanded={openCat}
              aria-haspopup="true"
            >
              Categories <FontAwesomeIcon icon={faChevronDown} />
            </button>

            {openCat && (
              <div className="hd-mega" role="menu" aria-label="Antivirus categories">
                <div className="hd-mega-grid">
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      className="hd-mega-item"
                      onClick={() => handleCategoryNavigation(c)}
                      role="menuitem"
                    >
                      <span className="hd-bullet">{c.id}</span>
                      <span className="hd-cat">{c.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button className="hd-link" onClick={() => go("/whychooseus")}>
            Why Choose Us
          </button>
          <button className="hd-link" onClick={() => go("/about")}>About</button>
          <button className="hd-link" onClick={() => go("/contact")}>Contact</button>
        </nav>

        {/* ACTIONS */}
        <div className="hd-acts">
          <a className="hd-call" href={`tel:${webinfo?.phonecall || webinfo?.phone || ""}`}>
            <FontAwesomeIcon icon={faPhone} />
            <span>{webinfo?.phone || "Call an expert"}</span>
          </a>

          <button className="hd-cart" onClick={() => go("/your-cart")} aria-label="Open cart">
            <FontAwesomeIcon icon={faShoppingCart} />
            {cartLength > 0 && <span className="hd-badge">{cartLength}</span>}
          </button>

          <button className="hd-burger" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <FontAwesomeIcon icon={faBars} />
          </button>
        </div>
      </div>

      {/* MOBILE DRAWER */}
      <div className={`hd-drawer ${mobileOpen ? "open" : ""}`} aria-hidden={!mobileOpen}>
        <div className="hd-drawer-head">
          <button className="hd-logo" onClick={() => go("/")}>
            {webinfo?.logo ? (
              <img src={webinfo.logo} alt={webinfo.name || "Logo"} />
            ) : (
              <span>{webinfo?.name || "SecureHub"}</span>
            )}
          </button>
          <button className="hd-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="hd-drawer-body">
          <button className="hd-mob-link" onClick={() => go("/")}>Home</button>

          <details className="hd-mob-acc">
            <summary>Categories</summary>
            <div className="hd-mob-grid">
              {categories.map((c) => (
                <button
                  key={c.id}
                  className="hd-mob-item"
                  onClick={() => handleCategoryNavigation(c)}
                >
                  <span className="hd-bullet">{c.id}</span>
                  <span className="hd-cat">{c.name}</span>
                </button>
              ))}
            </div>
          </details>

          <button className="hd-mob-link" onClick={() => go("/whychooseus")}>
            Why Choose Us
          </button>
          <button className="hd-mob-link" onClick={() => go("/about")}>About</button>
          <button className="hd-mob-link" onClick={() => go("/contact")}>Contact</button>

          <a className="hd-mob-call" href={`tel:${webinfo?.phonecall || webinfo?.phone || ""}`}>
            <FontAwesomeIcon icon={faPhone} />
            <span>{webinfo?.phone || "Call an expert"}</span>
          </a>
        </div>
      </div>

      {/* drawer backdrop */}
      <button
        className={`hd-backdrop ${mobileOpen ? "show" : ""}`}
        onClick={() => setMobileOpen(false)}
        aria-hidden={!mobileOpen}
        tabIndex={-1}
      />
    </header>
  );
};

export default Header;
